<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        :block="$vuetify.breakpoint.smAndDown"
        color="success" 
        class="px-10"
        v-on="on"
      >
        Apply Leave
      </v-btn>
    </template>
    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="green"
      title="Leave"
      text="Apply Leave"
      class="mb-0"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row no-gutters>
            <v-col cols="12">
              <v-autocomplete
                v-model="leaveType"
                v-validate="{
                  required:true,
                  isValidLeaveType:leaveType
                }"
                :error-messages="errors.collect('leave_type_id')"
                :items="leaveTypes"
                data-vv-name="leave_type_id"
                data-vv-as="leave type"
                item-text="name"
                return-object
                label="Leave Type"
                @input="leaveTypeChanged"
              />     
            </v-col>
          </v-row>

          <v-row
            v-show="leaveType.requires_document"
            no-gutters
          >
            <v-col cols="12">
              <helper-file-input
                v-model="file" 
                v-validate="fileRules"
                :max-size-in-mb="2"
                :max-number="5"
                :multiple="true"
                data-vv-name="file"
                field-name="file"
                accept-type=".pdf"    
                extension="ext:pdf"         
              >
                <div 
                  slot="activator"
                  class="mb-2"
                >   
                  <v-row>
                    <v-col
                      :cols="`${file ? 2 : 6}`"
                    >
                      <v-btn                    
                        :disabled="progressing"
                        class="mx-2"    
                      >
                        Add file
                      </v-btn>
                      <span> 
                        {{ file ? '' : 'No file chosen (.pdf only)' }}
                      </span>
                    </v-col>
                    <v-col
                      v-if="file"
                      cols="10"
                      class="px-2"
                    >
                      <span
                        v-for="(selectedFile, i) in file.selectedFiles"
                        :key="selectedFile.name"
                        class="font-weight-bold"
                      > {{ selectedFile.name }} {{ (i + 1) === file.selectedFiles.length ? '' : ',' }} </span>
                    </v-col>
                  </v-row>
                </div>
              </helper-file-input>
              <span class="red--text text--lighten-1 caption">{{ errors.first('file') }}</span>
            </v-col>
          </v-row>

          <v-row
            v-if="!_.isEmpty(leaveType)"
            no-gutters
          >
            <v-col
              cols="12"
              md="6"
            >
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDate"
                    v-validate="'required'"
                    label="Start Date"
                    prepend-icon="event"
                    :error-messages="errors.collect('start_date')"
                    data-vv-name="start_date"  
                    data-vv-as="start date"      
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="earliestDate"
                  @input="startDateChanged"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDate"
                    v-validate="'required'"
                    :disabled="leaveType.is_continuous ? true : false"
                    label="End Date"
                    prepend-icon="event"
                    :error-messages="errors.collect('end_date')"
                    data-vv-name="end_date"  
                    data-vv-as="end date"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"                  
                  :min="startDate ? startDate : earliestDate"
                  :max="latestDate"
                  @input="endDateChanged"
                />
              </v-menu>
            </v-col>
          </v-row>
          
          <v-row no-gutters>
            <v-col cols="12">
              <i style="font-size:12px">{{ leaveHint }}</i>
            </v-col>
          </v-row>
          <v-card
            v-if="datesSelected && !leaveType.is_continuous"
            class="elevation-1"
            max-height="280px"
            data-simplebar
          >            
            <v-container class="pa-0">
              <v-progress-linear
                v-if="leaveDays.length < 1"
                indeterminate
                color="primary"
              />
              <v-row
                v-for="(item, i) in leaveDays"
                v-else
                :key="i"
                justify="center"
                align="center"
                no-gutters
              >
                <v-col              
                  cols="12"
                  md="4"
                >
                  <span class="font-weight-medium">
                    {{ item.date }} ({{ $moment(item.date).format('ddd') }}) :
                  </span>
                </v-col>
                <v-col              
                  cols="12"
                  md="7"
                >
                  <v-radio-group
                    v-model="leaveDays[i].leave_session_id"
                    row
                    :disabled="leaveDays[i].disabled"
                  >
                    <v-radio
                      v-for="session in leaveSessions"
                      :key="session.id"
                      :label="session.name"
                      :value="session.id"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-container>
          </v-card>

          <v-row v-if="(leaveType.requires_reason && showReason)||backDatedLeave">
            <v-col cols="12">
              <v-textarea
                v-model="reason"
                v-validate="'required|max:5000'"
                :rows="2"
                :error-messages="errors.collect('reason')"
                data-vv-name="reason"        
                label="Reason"
              />
            </v-col>
          </v-row>
        </v-container>
      </form>
      <v-card-actions>
        <div>
          <p
            v-if="datesSelected"
            class="font-weight-bold"
          >
            Leave Requested: {{ leaveDaysRequested }} Day(s)
          </p>
          <p class="red--text text--lighten-1 caption">
            {{ errorMessage }}
          </p>
        </div>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="closeDialog"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="loading"
          color="success"          
          @click="submitForm"
        >
          Apply
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex'

  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
    },
    inject: ['$validator'],  
    data () {
      return {
        dialog: null,
        file: null,
        loading: false,
        progressing: false,
        leaveSessions: [],
        workDays: [],
        holidayDetails: [],
        leaveType: {},
        reason: null,
        startDateMenu: null,
        startDate: null,
        endDateMenu: null,
        endDate: null,
        leaveEntitlements: [],
        leaveDays: [],
        leaveSession: {
          '2': 1,
          '3': 2,
          '4': 3,
          '1': 4,
        },
        workSession: {
          '2': 2,
          '3': 3,
          '4': 4,
          '1': 4,

        },
      }
    },

    computed: {      
      ...mapState({ employee: 'currentEmployee' }),
      datesSelected () {
        return this.startDate && this.endDate && this.noOfSelectedDays > 0
      },      
      errorMessage () {
        if (!this.selectedEntitlement) return
        let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
        if (this.selectedEntitlement.available_days - carriedOverDays < this.leaveDaysRequested) return '*Not enough available leaves'
            
        return ''
      },  
      leaveDaysRequested () {
        return this.leaveDays.reduce((accumulator, leaveDay) => {
          const day = leaveDay.leave_session_id === 4 ? 1 : 0.5
          
          return accumulator + day
        }, 0)
      },  
      noOfSelectedDays () {
        const startDate = this.$moment(this.startDate)
        const endDate = this.$moment(this.endDate)

        return endDate.diff(startDate, 'days') + 1 // includes day 1
      },     
      selectedEntitlement () {
        if (!this.startDate) return
        return this.leaveEntitlements.find(entitlement => entitlement.leave_type_id === this.leaveType.id && entitlement.year === parseInt(this.startDate.substring(0, 4))) || {}
      },
      carriedOverEntitlementDays () {
        if (!this.startDate) return

        return this.leaveEntitlements.find(entitlement => entitlement.leave_type_id === this.leaveType.id && entitlement.year === (parseInt(this.startDate.substring(0, 4)) + 1))
      },
      leaveTypes () {
        let leaveTypes = []
        this.leaveEntitlements.map(entitlement => {
          leaveTypes.push(entitlement.leave_type)
        })
        return _.orderBy(_.uniqBy(leaveTypes, 'id'), ['name'], ['asc'])
      },
      workingDays () {
        return this.workDays.filter(workDay => workDay.work_session.start_time !== workDay.work_session.end_time)
      },
      holidayDates () {
        return this.holidayDetails.filter(holidayDetail => {
          if (holidayDetail.holiday.is_national) return true
          
          if (holidayDetail.holiday_states.length > 0) {
            return holidayDetail.holiday_states.some(state => state.state_id === this.employee.city.state_id && state.department_id === this.employee.department_id) 
          }
          return false
        })
          .map(holidayDetail => holidayDetail.date)
      },
      earliestDate () {
        return this.$moment().startOf('year').format('YYYY-MM-DD')
      },
      latestDate () {
        return this.$moment(this.startDate).endOf('year').format('YYYY-MM-DD')
      },
      fileRules () {
        return this.leaveType.requires_document ? 'required' : ''
      },
      leaveHint () {
        let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
        return _.isEmpty(this.startDate) ? '' : `*Leave Available: ${this.roundedTwoDecimal(this.selectedEntitlement.available_days - carriedOverDays)} Day(s)`
      },
      showReason () {
        if (!this.leaveType || !this.startDate) return false
        
        return this.$moment(this.startDate).diff(this.$moment(), 'days') + 1 <= this.leaveType.minimum_days_reason_required
      },
      backDatedLeave () {
        if (!this.leaveType || !this.startDate) return false
        
        if (this.$moment(this.startDate).diff(this.$moment(), 'days') + 1 <= 0) {
          return true
        }
        return false
      },
    },
    watch: {
      dialog (value) {
        if (value) {
          let scope = this

          this.loadData()
          this.$validator.resume()

          this.$validator.extend('isValidLeaveType', {
            getMessage (field, args) {
              let leaveType = null
              if (args.id === 3) {
                leaveType = scope.leaveTypes.find(type => type.id === 15) // UL2
              } else if (args.id === 15) {
                leaveType = scope.leaveTypes.find(type => type.id === 3) // UL
              }
              
              return 'Please select ' + leaveType.name.toUpperCase()   
            },
            validate (value, args) {
              return scope.isValidateLeaveType(value) 
            },
          })
        } else {
          this.$resetData()
        }
      },
    },

    methods: {
      roundedTwoDecimal (value) {
        return Math.round(value * 100, 0) / 100
      },
      isValidateLeaveType (value) {
        if (value.id === 3 && this.leaveDaysRequested > 14) { // unpaid leave
          return false
        } else if (value.id === 15 && this.leaveDaysRequested <= 14 && this.leaveDaysRequested > 0) { // unpaid leave 2
          return false
        }

        return true
      },
      roundLeave (days) {
        return (days - Math.floor(days)) >= 0.5 ? Math.floor(days) + 0.5 : Math.floor(days)
      },
      closeDialog () {
        this.$validator.pause()
        this.dialog = false
      },
      loadData () {
        this.loading = true

        Promise.all([
          this.loadLeaveSessions(),
          this.loadWorkDays(),
          this.loadEntitlements(),
          this.loadLeaveApplications(),
        ]).then(([leaveSession, workDay, entitlement, leaveApplication]) => {
          this.leaveSessions = leaveSession.data.filter(session => session.name.toUpperCase() !== 'NONE')
          this.workDays = workDay.data

          let annualLeaveApplications = null
          let sickLeaveApplications = null
          let unpaidLeaveApplications = null
          let maternityLeaveApplications = null
          let paternityLeaveApplications = null
          let compassionateLeaveApplications = null
          let nextYearAnnualLeaveApplications = null
          let nextYearSickLeaveApplications = null
          let nextYearMaternityLeaveApplications = null
          let nextYearUnpaidLeaveApplications = null
          let nextYearPaternityLeaveApplications = null
          let nextYearCompassionateLeaveApplications = null

          let annualLeaveCounter = 0
          let sickLeaveCounter = 0
          let maternityLeaveCounter = 0
          let paternityLeaveCounter = 0
          let compassionateLeaveCounter = 0
          let unpaidLeaveCounter = 0
          let nextYearAnnualLeaveCounter = 0
          let nextYearSickLeaveCounter = 0
          let nextYearMaternityLeaveCounter = 0
          let nextYearPaternityLeaveCounter = 0
          let nextYearCompassionateLeaveCounter = 0
          let nextYearUnpaidLeaveCounter = 0

          leaveApplication.data.map(item => {
            if (item.leave_type.name.toLowerCase().includes('annual') && item.year === (new Date()).getFullYear()) {
              annualLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('annual') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearAnnualLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === (new Date()).getFullYear()) {
              sickLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearSickLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === (new Date()).getFullYear()) {
              unpaidLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearUnpaidLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === (new Date()).getFullYear()) {
              maternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearMaternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === (new Date()).getFullYear()) {
              paternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearPaternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === (new Date()).getFullYear()) {
              compassionateLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearCompassionateLeaveApplications = item.leave_applications
            }
          })

          if (annualLeaveApplications) { annualLeaveCounter = this.getPendingApplications(annualLeaveApplications) }
          if (nextYearAnnualLeaveApplications) { nextYearAnnualLeaveCounter = this.getPendingApplications(nextYearAnnualLeaveApplications) }

          if (sickLeaveApplications) { sickLeaveCounter = this.getPendingApplications(sickLeaveApplications) }
          if (nextYearSickLeaveApplications) { nextYearSickLeaveCounter = this.getPendingApplications(nextYearSickLeaveApplications) }

          if (unpaidLeaveApplications) { unpaidLeaveCounter = this.getPendingApplications(unpaidLeaveApplications) }
          if (nextYearUnpaidLeaveApplications) { nextYearUnpaidLeaveCounter = this.getPendingApplications(nextYearUnpaidLeaveApplications) }

          if (maternityLeaveApplications) { maternityLeaveCounter = this.getPendingApplications(maternityLeaveApplications) }
          if (nextYearMaternityLeaveApplications) { nextYearMaternityLeaveCounter = this.getPendingApplications(nextYearMaternityLeaveApplications) }

          if (paternityLeaveApplications) { paternityLeaveCounter = this.getPendingApplications(paternityLeaveApplications) }
          if (nextYearPaternityLeaveApplications) { nextYearPaternityLeaveCounter = this.getPendingApplications(nextYearPaternityLeaveApplications) }

          if (compassionateLeaveApplications) { compassionateLeaveCounter = this.getPendingApplications(compassionateLeaveApplications) }
          if (nextYearCompassionateLeaveApplications) { nextYearCompassionateLeaveCounter = this.getPendingApplications(nextYearCompassionateLeaveApplications) }

          entitlement.data.map(item => {
            if (item.leave_type.name.toLowerCase().includes('annual') && item.year === (new Date()).getFullYear()) {
              item.available_days = item.available_days - annualLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('annual') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearAnnualLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === (new Date()).getFullYear()) {
              item.available_days = item.available_days - sickLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearSickLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === (new Date()).getFullYear()) {
              item.available_days = item.available_days - unpaidLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearUnpaidLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === (new Date()).getFullYear()) {
              item.available_days = item.available_days - maternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearMaternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === (new Date()).getFullYear()) {
              item.available_days = item.available_days - paternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearPaternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === (new Date()).getFullYear()) {
              item.available_days = item.available_days - compassionateLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearCompassionateLeaveCounter
            }
          })

          let annualLeave = entitlement.data.find(entitlement => entitlement.leave_type_id === 1) // annual leave
          let unpaidLeave = entitlement.data.find(entitlement => entitlement.leave_type_id === 3) // unpaid leave

          if (annualLeave && unpaidLeave) {
            if (annualLeave.available_days < 0.5) {
              this.leaveEntitlements = entitlement.data.filter(entitlement => {
                return entitlement.leave_type_id !== 1
              })
            } else {
              this.leaveEntitlements = entitlement.data.filter(entitlement => {
                return entitlement.leave_type_id !== 3
              })
            }
          } else {
            this.leaveEntitlements = entitlement.data
          }

          this.loading = false
        })
      },
      startDateChanged () {
        Promise.all([
          this.loadHolidays(),
        ]).then(([holiday]) => {
          this.holidayDetails = holiday.data
          this.clearLeaveDays()
          this.computeLeaveDates()
          this.computeLeaveDays()

          this.$validator.validate('leave_type_id')
        })
      },
      getComputedEndDate (totalDays) {
        let getDate = this.$moment(this.startDate)
        for (var i = 0; i <= totalDays; i++) {
          getDate = i < 0 ? this.$moment(this.startDate) : this.$moment(this.startDate).add(i, 'days').format('YYYY-MM-DD')
          const isTodayHoliday = this.holidayDates.includes(this.$moment(getDate).format('YYYY-MM-DD'))
          if ((!this.leaveType.includes_off_day && this.$moment(getDate).day() === 6) || 
            (!this.leaveType.includes_rest_day && this.$moment(getDate).day() === 0) ||
            (!this.leaveType.includes_public_holiday && isTodayHoliday)) {
            totalDays = totalDays + 1
          }
        }
        return this.$moment(getDate).format('YYYY-MM-DD')
      },
      computeLeaveDates () {
        this.startDateMenu = false
        // let totalDays = this.$moment(this.startDate).add(1, 'days').diff(this.$moment(this.startDate), 'days') + 1
        this.endDate = this.startDate
        
        if (this.leaveType.is_continuous) {
          let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
          
          let totalDays = this.selectedEntitlement.available_days - carriedOverDays - 1
          this.endDate = this.getComputedEndDate(totalDays)
        }
      },
      endDateChanged () {
        this.endDateMenu = false

        this.clearLeaveDays()
        this.computeLeaveDays()

        this.$validator.validate('leave_type_id')
      },
      leaveTypeChanged () {
        this.startDate = null
        this.endDate = null
        this.file = null
        this.reason = null

        this.clearLeaveDays()
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            if (this.errorMessage) return          

            this.progressing = true

            this.$api.leaveApplications().create(this.selectedEntitlement.id, this.prepareFormData())
              .then(response => {
                this.$emit('created')
                this.$snack({ message: 'Leave Applied' })

                this.closeDialog()
              })
              .catch(() => {})
              .then(() => {
                this.progressing = false
              })
          })
      },   
      loadEntitlements () {
        return this.$api.myLeaveEntitlements().list()
      },
      loadLeaveApplications () {
        return this.$api.leaveApplications().list(this.employee.employee_uid)
      },
      loadLeaveSessions () {
        return this.$api.leaveSessions().list()
      },      
      loadWorkDays () {
        return this.$api.employeeWorkdays().list(this.employee.employee_uid)
      },
      loadHolidays () {
        return this.$api.holidayDetails().list('?filter[year]=' + this.startDate.substring(0, 4))
      },
      prepareFormData () {
        if (this.file) {
          this.file.formData.set('start_date', this.startDate)
          this.file.formData.set('end_date', this.endDate)
          this.file.formData.set('reason', this.reason ? this.reason : 'Personal Matters')
          this.file.formData.set('leave_days', JSON.stringify(this.leaveDays))

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('start_date', this.startDate)
        formData.append('end_date', this.endDate)
        formData.append('reason', this.reason ? this.reason : 'Personal Matters')
        formData.append('leave_days', JSON.stringify(this.leaveDays))

        return formData
      },
      computeLeaveDays () {
        if (!this.datesSelected) return 
        
        let leaveDates = this.getLeaveDates(this.startDate, this.endDate, this.noOfSelectedDays)

        leaveDates.filter(leaveDate => {
          const isTodayHoliday = this.holidayDates.includes(leaveDate)
          if (this.leaveType.is_continuous || this.leaveType.includes_rest_day || this.leaveType.includes_off_day || this.leaveType.includes_public_holiday) {
            // restday
            if (this.$moment(leaveDate).day() === 0) {
              if (this.leaveType.includes_rest_day) {
                return leaveDate
              } else {
                return false
              }
            }
          
            // public holiday
            if (isTodayHoliday) {
              if (this.leaveType.includes_public_holiday) {
                return leaveDate
              } else {
                if (this.$moment(leaveDate).day() === 0) {
                  if (this.leaveType.includes_rest_day) {
                    return leaveDate
                  } else {
                    return false
                  }
                } else {
                  return false
                }
              }
            }

            // off day
            if (this.$moment(leaveDate).day() === 6) {
              if (this.leaveType.includes_off_day) {
                return leaveDate
              } else {
                return false
              }
            }

            return leaveDate
          } else {
            // leave date filter by work day
            const isWorkDay = this.workingDays.find(workingDay => {
              const isoWorkingDay = workingDay.day === 7 ? 0 : workingDay.day
            
              return isoWorkingDay === this.$moment(leaveDate).day()
            })
            
            if (isWorkDay && !isTodayHoliday) return leaveDate
          }
        }).map((leaveDate, i) => {
          if (this.leaveType.is_continuous) {
            this.leaveDays.push({
              date: leaveDate,
              leave_session_id: 4,
              disabled: true,
            })
          } else {
            this.workDays.map(item => {
              const isoWorkingDay = item.work_session.day === 7 ? 0 : item.work_session.day
            
              if (this.$moment(leaveDate).day() === isoWorkingDay) {
                this.leaveDays.push({
                  date: leaveDate,
                  leave_session_id: this.workSession[item.leave_session.id],
                  disabled: item.leave_session.name.toUpperCase() !== 'NONE',
                })
              }
            })
          }
        })      
      },
      getLeaveDates (selectedStartDate, selectedEndDate, interval) {
        let dateArray = []
        let startDate = this.$moment(selectedStartDate)
        const endDate = this.$moment(selectedEndDate)

        while (startDate <= endDate) {
          dateArray.push(this.$moment(startDate).format('YYYY-MM-DD'))

          startDate = this.$moment(startDate).add(1, 'days')
        }
        
        return dateArray
      },  
      clearLeaveDays () {
        this.leaveDays = []
      },      
      getPendingApplications (leaveApplications) {
        let sum = 0
        leaveApplications.map(application => {
          if (application.approval_request.approvals[application.approval_request.approvals.length - 1].status.id === 100) {
            sum = sum + application.requested_days
          }
        })

        return sum
      },
    },
  }
</script>

<style lang="css" scoped>
  .card-overflow {
    height: 40vh;
    overflow-y: scroll;
  }
  ::-webkit-scrollbar {
    width: 0px;  /* Remove scrollbar space */
    background: transparent;  /* Optional: just make scrollbar invisible */
  }
</style>
